import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Category, CreateCategoryRequest, CreateItemRequest, Item, UpdateCategoryRequest, UpdateItemRequest } from '../types';
import { apiClient } from '../services/api';
import { restorePaginatedQueries, snapshotPaginatedQueries, updatePaginatedQueries, PaginatedSnapshot } from '../utils/queryCache';

type SaveItemInput = { id?: string; data: CreateItemRequest | UpdateItemRequest };
type SaveCategoryInput = { id?: string; data: CreateCategoryRequest | UpdateCategoryRequest };
type ItemMutationContext = { snapshots: PaginatedSnapshot<Item> };

export const useItemMutations = () => {
  const queryClient = useQueryClient();
  const invalidateItems = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['items'] }),
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    queryClient.invalidateQueries({ queryKey: ['admin'] }),
  ]);

  const saveItemMutation = useMutation<Item, unknown, SaveItemInput, ItemMutationContext | undefined>({
    mutationFn: ({ id, data }) => id
      ? apiClient.updateItem(id, data as UpdateItemRequest)
      : apiClient.createItem(data as CreateItemRequest),
    onMutate: async ({ id, data }) => {
      if (!id) return undefined;
      await queryClient.cancelQueries({ queryKey: ['items'] });
      const snapshots = snapshotPaginatedQueries<Item>(queryClient, 'items');
      updatePaginatedQueries<Item>(queryClient, 'items', page => ({
        ...page,
        content: page.content.map(item => item.id === id ? { ...item, ...data } : item),
      }));
      return { snapshots };
    },
    onError: (_error, _input, context) => {
      if (context?.snapshots) restorePaginatedQueries(queryClient, context.snapshots);
    },
    onSettled: invalidateItems,
  });

  const deleteItemMutation = useMutation<void, unknown, string, { snapshots: PaginatedSnapshot<Item> }>({
    mutationFn: id => apiClient.deleteItem(id),
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey: ['items'] });
      const snapshots = snapshotPaginatedQueries<Item>(queryClient, 'items');
      updatePaginatedQueries<Item>(queryClient, 'items', page => ({
        ...page,
        content: page.content.filter(item => item.id !== id),
        totalElements: Math.max(0, page.totalElements - 1),
      }));
      return { snapshots };
    },
    onError: (_error, _id, context) => {
      if (context) restorePaginatedQueries(queryClient, context.snapshots);
    },
    onSettled: invalidateItems,
  });

  const saveCategoryMutation = useMutation<Category, unknown, SaveCategoryInput>({
    mutationFn: ({ id, data }) => id
      ? apiClient.updateCategory(id, data as UpdateCategoryRequest)
      : apiClient.createCategory(data as CreateCategoryRequest),
    onSuccess: invalidateItems,
  });

  const deleteCategoryMutation = useMutation<void, unknown, string>({
    mutationFn: id => apiClient.deleteCategory(id),
    onSuccess: invalidateItems,
  });

  return { saveItemMutation, deleteItemMutation, saveCategoryMutation, deleteCategoryMutation };
};
